/**
 * Recharts renderer — supports 2D chart types (line, scatter, bar, histogram, area).
 */

import { useMemo, useRef, useCallback } from 'react';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
} from 'recharts';
import type { ChartRendererProps } from '../types';
import { PALETTES, createDefaultMapping, MARKER_SHAPE_OPTIONS } from '../types';
import { getLabels, getUniqueLabels, isFieldNumeric, binValues, linearRegression, isClAlphaAxes } from '../visualMappingUtils';
import { useDataPointContextMenu, DataPointContextMenu } from '../../shared/DataPointContextMenu';

function getPalette(name: string): string[] {
  return PALETTES[name] ?? PALETTES['FlexCompute']!;
}

function getColor(pal: string[], _key: string | undefined, idx: number): string {
  return pal[idx % pal.length]!;
}

function groupData(data: Record<string, unknown>[], groupBy?: string) {
  if (!groupBy) return new Map([['All', data]]);
  const groups = new Map<string, Record<string, unknown>[]>();
  for (const row of data) {
    const key = String(row[groupBy] ?? 'Unknown');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }
  return groups;
}

function buildCompositeGroups(
  data: Record<string, unknown>[],
  config: ChartRendererProps['config'],
  pal: string[],
): { key: string; rows: Record<string, unknown>[]; color: string; shape: string }[] {
  const cm = config.colorMapping ?? createDefaultMapping();
  const sm = config.shapeMapping ?? createDefaultMapping();
  const hasColor = cm.mode === 'byVariable' && cm.field;
  const hasShape = sm.mode === 'byVariable' && sm.field;

  if (!hasColor && !hasShape) {
    const groups = groupData(data, config.groupBy);
    return [...groups.entries()].map(([key, rows], idx) => ({
      key, rows, color: getColor(pal, key, idx), shape: config.markerShape,
    }));
  }

  const colorLabels = hasColor ? getLabels(data, cm) : data.map(() => '_');
  const shapeLabels = hasShape ? getLabels(data, sm) : data.map(() => '_');
  const groupLabels = config.groupBy ? data.map((r) => String(r[config.groupBy!] ?? 'Unknown')) : data.map(() => '_');

  const uniqueColors = hasColor ? getUniqueLabels(colorLabels) : ['_'];
  const colorMap = new Map<string, string>();
  uniqueColors.forEach((lbl, i) => colorMap.set(lbl, pal[i % pal.length]!));

  const SHAPE_MAP: Record<string, string> = {
    circle: 'circle', square: 'square', diamond: 'diamond',
    triangle: 'triangle', cross: 'cross', x: 'star',
  };
  const uniqueShapes = hasShape ? getUniqueLabels(shapeLabels) : ['_'];
  const shapeAssign = new Map<string, string>();
  uniqueShapes.forEach((lbl, i) => shapeAssign.set(lbl, SHAPE_MAP[MARKER_SHAPE_OPTIONS[i % MARKER_SHAPE_OPTIONS.length]!] ?? 'circle'));

  const composite = new Map<string, { rows: Record<string, unknown>[]; color: string; shape: string }>();
  for (let i = 0; i < data.length; i++) {
    const cLabel = colorLabels[i]!;
    const sLabel = shapeLabels[i]!;
    const gLabel = groupLabels[i]!;
    const compositeKey = [gLabel, cLabel, sLabel].filter((v) => v !== '_').join(' | ');
    if (!composite.has(compositeKey)) {
      composite.set(compositeKey, {
        rows: [],
        color: hasColor ? colorMap.get(cLabel)! : getColor(pal, gLabel, [...new Set(groupLabels)].indexOf(gLabel)),
        shape: hasShape ? shapeAssign.get(sLabel)! : config.markerShape,
      });
    }
    composite.get(compositeKey)!.rows.push(data[i]!);
  }

  return [...composite.entries()].map(([key, val]) => ({ key, ...val }));
}

function getDashArray(style: string): string | undefined {
  switch (style) {
    case 'dashed': return '8 4';
    case 'dotted': return '2 4';
    case 'dashdot': return '8 4 2 4';
    default: return undefined;
  }
}

const HIGHLIGHT_COLOR = '#FFD700';
const FLASH_STYLE = `
@keyframes flash-point {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}
`;

export function RechartsRenderer({ data: rawData, config, theme, mini = false, onPointClick, selectedNodeId }: ChartRendererProps) {
  const ctxMenu = useDataPointContextMenu(rawData);
  const {
    chartType, xAxis, yAxis, groupBy, title, showGrid, showLegend,
    markerSize, markerOpacity, lineStyle, lineWidth,
    barOpacity, histBins,
    palette, legendPosition,
    trendLine, showMean, showMedian,
    showLiftCurveSlope,
    plotPadding, titleSize, titleWeight,
  } = config;

  const data = useMemo(() => {
    const xField = xAxis.field;
    const yField = yAxis.field;
    return rawData
      .map((row) => {
        const xRaw = row[xField];
        const yRaw = row[yField];
        const xNum = xRaw != null ? Number(xRaw) : NaN;
        const yNum = yRaw != null ? Number(yRaw) : NaN;
        if (isNaN(xNum) || isNaN(yNum)) return null;
        if (xRaw === xNum && yRaw === yNum) return row;
        return { ...row, [xField]: xNum, [yField]: yNum };
      })
      .filter((r): r is Record<string, unknown> => r !== null);
  }, [rawData, xAxis.field, yAxis.field]);

  const pal = getPalette(palette);
  const textColor = theme === 'dark' ? '#A3ADA7' : '#4A524D';
  const gridColor = theme === 'dark' ? '#2A302C' : '#E6E9E7';
  const annotColor = theme === 'dark' ? '#FF6B6B88' : '#E5484D88';
  const medianColor = theme === 'dark' ? '#6DB5DD88' : '#457B9D88';

  const groups = groupData(data, groupBy);

  const tooltipStyle = {
    background: theme === 'dark' ? '#1C211E' : '#FFFFFF',
    border: `1px solid ${gridColor}`, borderRadius: 6, fontSize: 12, color: textColor,
  };

  const xLabel = xAxis.label || xAxis.field;
  const yLabel = yAxis.label || yAxis.field;
  const margin = mini
    ? { top: 4, right: 4, bottom: 4, left: 4 }
    : { top: 10, right: plotPadding / 2, bottom: 30, left: plotPadding / 2 };
  const legendWrap: React.CSSProperties = { fontSize: 11 };
  const legendAlign = legendPosition === 'top' || legendPosition === 'bottom' ? legendPosition : 'bottom';
  const effectiveLineWidth = mini ? 2.5 : lineWidth;
  const effectiveMarkerSize = mini ? 8 : markerSize;
  const tickStyle = mini ? { fill: textColor, fontSize: 8 } : { fill: textColor, fontSize: 11 };
  const xAxisLabel = mini ? undefined : { value: xLabel, position: 'bottom' as const, fill: textColor, fontSize: 12 };
  const yAxisLabel = mini ? undefined : { value: yLabel, angle: -90, position: 'insideLeft' as const, fill: textColor, fontSize: 12 };
  const containerPadding = mini ? 0 : 8;
  const showGridHere = !mini && showGrid;
  const showStatLines = !mini && (showMean || showMedian);

  const allY = data.map((r) => Number(r[yAxis.field])).filter((v) => !isNaN(v));
  const mean = allY.length > 0 ? allY.reduce((a, b) => a + b, 0) / allY.length : undefined;
  const median = allY.length > 0
    ? (() => { const s = [...allY].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2; })()
    : undefined;

  const dropped = rawData.length - data.length;

  const titleEl = (
    <div style={{ textAlign: 'center', marginBottom: 4 }}>
      {title && (
        <div style={{
          fontSize: titleSize, fontWeight: titleWeight === 'bold' ? 700 : titleWeight === 'light' ? 300 : 400,
          color: textColor,
        }}>
          {title}
        </div>
      )}
      {dropped > 0 && (
        <div style={{ fontSize: 10, color: theme === 'dark' ? '#FF6B6B' : '#E5484D' }}>
          {data.length} of {rawData.length} rows plotted — {dropped} rows missing {xAxis.field} or {yAxis.field}
        </div>
      )}
    </div>
  );

  const statLines = showStatLines ? (
    <>
      {showMean && mean != null && (
        <ReferenceLine y={mean} stroke={annotColor} strokeDasharray="8 4" strokeWidth={1.5}>
          <Label value="mean" position="insideTopRight" fill={textColor} fontSize={10} />
        </ReferenceLine>
      )}
      {showMedian && median != null && (
        <ReferenceLine y={median} stroke={medianColor} strokeDasharray="2 4" strokeWidth={1.5}>
          <Label value="median" position="insideTopRight" fill={textColor} fontSize={10} />
        </ReferenceLine>
      )}
    </>
  ) : null;

  const compositeGroups = buildCompositeGroups(data, config, pal);

  const szMapping = config.sizeMapping ?? createDefaultMapping();
  const hasSizeMapping = szMapping.mode === 'byVariable' && szMapping.field;
  if (hasSizeMapping) {
    const field = szMapping.field!;
    const numeric = isFieldNumeric(data, field);
    if (numeric && !szMapping.bin) {
      const vals = data.map((r) => Number(r[field]));
      const lo = Math.min(...vals.filter((v) => !isNaN(v)));
      const hi = Math.max(...vals.filter((v) => !isNaN(v)));
      const span = hi - lo || 1;
      for (const row of data) {
        const v = Number(row[field]);
        (row as Record<string, unknown>).__sz = isNaN(v) ? markerSize : 3 + ((v - lo) / span) * 27;
      }
    } else {
      const labels = szMapping.bin && numeric
        ? binValues(data.map((r) => Number(r[field])), szMapping.binCount)
        : data.map((r) => String(r[field] ?? ''));
      const unique = getUniqueLabels(labels);
      const step = unique.length > 1 ? 27 / (unique.length - 1) : 0;
      const sizeMap = new Map<string, number>();
      unique.forEach((lbl, i) => sizeMap.set(lbl, 3 + i * step));
      for (let i = 0; i < data.length; i++) {
        (data[i] as Record<string, unknown>).__sz = sizeMap.get(labels[i]!) ?? markerSize;
      }
    }
  }

  const selRef = useRef(selectedNodeId);
  selRef.current = selectedNodeId;

  const ctxMenuRef = useRef(ctxMenu);
  ctxMenuRef.current = ctxMenu;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderDot = useCallback((props: any) => {
    const sz = hasSizeMapping ? (props.payload?.__sz ?? effectiveMarkerSize) : effectiveMarkerSize;
    const sel = selRef.current;
    const isSelected = sel && props.payload?._node_id === sel;
    const nodeId = props.payload?._node_id as string | undefined;
    return (
      <g
        onMouseEnter={() => {
          if (nodeId && props.payload) ctxMenuRef.current.setHoveredPoint(nodeId, props.payload);
        }}
        onMouseLeave={() => ctxMenuRef.current.clearHoveredPoint()}
      >
        {isSelected && (
          <circle cx={props.cx} cy={props.cy} r={sz / 2 + 6} fill="none"
            stroke={HIGHLIGHT_COLOR} strokeWidth={2} opacity={0.6}
            style={{ animation: 'flash-point 1s ease-in-out 3' }} />
        )}
        <circle cx={props.cx} cy={props.cy} r={isSelected ? sz / 2 + 2 : sz / 2}
          fill={isSelected ? HIGHLIGHT_COLOR : props.fill}
          opacity={isSelected ? 1 : markerOpacity} />
      </g>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, hasSizeMapping, effectiveMarkerSize, markerOpacity]);

  switch (chartType) {
    case 'scatter': {
      let scatterSlopeLabel: React.ReactNode = null;
      let trendLineData: Record<string, unknown>[] | null = null;
      const wantTrend = showLiftCurveSlope || trendLine === 'linear';
      const trendStroke = theme === 'dark' ? '#94a3b8' : '#64748b';

      if (wantTrend && compositeGroups.length > 0 && data.length >= 2) {
        const firstRows = compositeGroups[0]!.rows;
        const xs = firstRows.map((r) => Number(r[xAxis.field]));
        const ys = firstRows.map((r) => Number(r[yAxis.field]));
        const valid = xs.flatMap((x, i) => { const y = ys[i]; return y != null && !isNaN(x) && !isNaN(y) ? [{ x, y }] : []; });
        if (valid.length >= 2) {
          const { slope, intercept } = linearRegression(valid.map((p) => p.x), valid.map((p) => p.y));
          const sortedVx = valid.map((p) => p.x).sort((a, b) => a - b);
          const minX = sortedVx[0]!;
          const maxX = sortedVx[sortedVx.length - 1]!;
          trendLineData = [
            { [xAxis.field]: minX, [yAxis.field]: slope * minX + intercept },
            { [xAxis.field]: maxX, [yAxis.field]: slope * maxX + intercept },
          ];
          if (showLiftCurveSlope) {
            const isClAlpha = isClAlphaAxes(xAxis.field, yAxis.field);
            const label = isClAlpha ? 'Lift curve slope' : 'Slope';
            const unit = isClAlpha ? ' /deg' : '';
            scatterSlopeLabel = (
              <div style={{ fontSize: 11, color: textColor, marginBottom: 4 }}>
                {label}: <strong>{slope.toFixed(4)}{unit}</strong>
              </div>
            );
          }
        }
      }
      return (
        <div style={{ width: '100%', height: '100%', padding: containerPadding, minWidth: 0, minHeight: 0 }} onContextMenu={mini ? undefined : ctxMenu.handleContextMenu}>
          <style>{FLASH_STYLE}</style>
          {!mini && titleEl}
          {!mini && showLiftCurveSlope && scatterSlopeLabel}
          <ResponsiveContainer width="100%" height={mini ? '100%' : '95%'} minWidth={10} minHeight={10}>
            <ScatterChart margin={margin}>
              {showGridHere && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
              <XAxis dataKey={xAxis.field} type="number" name={xLabel} tick={tickStyle}
                label={xAxisLabel} scale={xAxis.scale === 'log' ? 'log' : 'auto'} />
              <YAxis dataKey={yAxis.field} type="number" name={yLabel} tick={tickStyle}
                label={yAxisLabel} scale={yAxis.scale === 'log' ? 'log' : 'auto'} />
              {!mini && <Tooltip contentStyle={tooltipStyle} />}
              {showLegend && <Legend wrapperStyle={legendWrap} verticalAlign={legendAlign} />}
              {statLines}
              {trendLineData && (
                <Scatter
                  name="Linear fit"
                  data={trendLineData}
                  line={{ stroke: trendStroke, strokeWidth: 1.5, strokeDasharray: '6 4' }}
                  fill="transparent"
                  shape={<circle r={0} />}
                  legendType="none"
                  isAnimationActive={false}
                />
              )}
              {compositeGroups.map(({ key, rows, color }) => (
                <Scatter key={key} name={key} data={rows} fill={color} opacity={markerOpacity}
                  shape={renderDot}
                  isAnimationActive={!mini} animationDuration={300}
                  onClick={onPointClick ? (entry: Record<string, unknown>) => onPointClick(entry) : undefined}
                  cursor={onPointClick ? 'pointer' : undefined}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
          {ctxMenu.menu && <DataPointContextMenu menu={ctxMenu.menu} onClose={ctxMenu.close} />}
        </div>
      );
    }

    case 'line': {
      const allKeys = [...groups.keys()];
      const merged = new Map<number, Record<string, unknown>>();
      for (const [key, rows] of groups) {
        for (const row of rows) {
          const xVal = Number(row[xAxis.field]);
          if (!merged.has(xVal)) merged.set(xVal, { [xAxis.field]: xVal });
          merged.get(xVal)![key] = row[yAxis.field];
        }
      }
      let mergedData = [...merged.values()].sort((a, b) => (a[xAxis.field] as number) - (b[xAxis.field] as number));

      let slopeLabel: React.ReactNode = null;
      if ((showLiftCurveSlope || trendLine === 'linear') && allKeys.length > 0 && mergedData.length >= 2) {
        const firstKey = allKeys[0]!;
        const xs = mergedData.map((r) => Number(r[xAxis.field]));
        const ys = mergedData.map((r) => Number(r[firstKey]));
        const valid = xs.flatMap((x, i) => { const y = ys[i]; return y != null && !isNaN(x) && !isNaN(y) ? [{ x, y }] : []; });
        if (valid.length >= 2) {
          const { slope, intercept } = linearRegression(valid.map((p) => p.x), valid.map((p) => p.y));
          mergedData = mergedData.map((r) => ({
            ...r,
            __trend: slope * Number(r[xAxis.field]) + intercept,
          }));
          const isClAlpha = isClAlphaAxes(xAxis.field, yAxis.field);
          const label = isClAlpha ? 'Lift curve slope' : 'Slope';
          const unit = isClAlpha ? ' /deg' : '';
          slopeLabel = (
            <div style={{ fontSize: 11, color: textColor, marginBottom: 4 }}>
              {label}: <strong>{slope.toFixed(4)}{unit}</strong>
            </div>
          );
        }
      }

      return (
        <div style={{ width: '100%', height: '100%', padding: containerPadding, minWidth: 0, minHeight: 0 }} onContextMenu={mini ? undefined : ctxMenu.handleContextMenu}>
          {!mini && titleEl}
          {!mini && showLiftCurveSlope && slopeLabel}
          <ResponsiveContainer width="100%" height={mini ? '100%' : '95%'} minWidth={10} minHeight={10}>
            <LineChart data={mergedData} margin={margin}>
              {showGridHere && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
              <XAxis dataKey={xAxis.field} type="number" tick={tickStyle}
                label={xAxisLabel} scale={xAxis.scale === 'log' ? 'log' : 'auto'} />
              <YAxis tick={tickStyle}
                label={yAxisLabel} scale={yAxis.scale === 'log' ? 'log' : 'auto'} />
              {!mini && <Tooltip contentStyle={tooltipStyle} />}
              {showLegend && <Legend wrapperStyle={legendWrap} verticalAlign={legendAlign} />}
              {statLines}
              {(showLiftCurveSlope || trendLine === 'linear') && (mergedData[0] as Record<string, unknown>)?.__trend != null && (
                <Line dataKey="__trend" stroke={theme === 'dark' ? '#94a3b8' : '#64748b'} strokeWidth={1.5} strokeDasharray="6 4" dot={false} name="Linear fit" />
              )}
              {allKeys.map((key, idx) => (
                <Line key={key} dataKey={key} stroke={getColor(pal, key, idx)} strokeWidth={effectiveLineWidth}
                  strokeDasharray={getDashArray(lineStyle)} dot={{ r: effectiveMarkerSize / 2 }}
                  isAnimationActive={!mini} animationDuration={300}
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  activeDot={onPointClick ? {
                    cursor: 'pointer',
                    onClick: (_e: unknown, payload: { payload?: Record<string, unknown> }) => { if (payload?.payload) onPointClick(payload.payload); },
                    onMouseEnter: (_e: unknown, payload: { payload?: Record<string, unknown> }) => {
                      const row = payload?.payload;
                      const nodeId = row?._node_id as string | undefined;
                      if (nodeId && row) ctxMenu.setHoveredPoint(nodeId, row);
                    },
                    onMouseLeave: () => ctxMenu.clearHoveredPoint(),
                  } as any : undefined}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          {ctxMenu.menu && <DataPointContextMenu menu={ctxMenu.menu} onClose={ctxMenu.close} />}
        </div>
      );
    }

    case 'area': {
      const allKeys = [...groups.keys()];
      const merged = new Map<number, Record<string, unknown>>();
      for (const [key, rows] of groups) {
        for (const row of rows) {
          const xVal = Number(row[xAxis.field]);
          if (!merged.has(xVal)) merged.set(xVal, { [xAxis.field]: xVal });
          merged.get(xVal)![key] = row[yAxis.field];
        }
      }
      const mergedData = [...merged.values()].sort((a, b) => (a[xAxis.field] as number) - (b[xAxis.field] as number));

      return (
        <div style={{ width: '100%', height: '100%', padding: containerPadding, minWidth: 0, minHeight: 0 }}>
          {!mini && titleEl}
          <ResponsiveContainer width="100%" height={mini ? '100%' : '95%'} minWidth={10} minHeight={10}>
            <AreaChart data={mergedData} margin={margin}>
              {showGridHere && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
              <XAxis dataKey={xAxis.field} type="number" tick={tickStyle} label={xAxisLabel} />
              <YAxis tick={tickStyle} label={yAxisLabel} />
              {!mini && <Tooltip contentStyle={tooltipStyle} />}
              {showLegend && <Legend wrapperStyle={legendWrap} verticalAlign={legendAlign} />}
              {statLines}
              {allKeys.map((key, idx) => {
                const c = getColor(pal, key, idx);
                return <Area key={key} dataKey={key} stroke={c} fill={`${c}33`} strokeWidth={effectiveLineWidth}
                  isAnimationActive={!mini} animationDuration={300} />;
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      );
    }

    case 'bar': {
      const allKeys = [...groups.keys()];
      const merged = new Map<string, Record<string, unknown>>();
      for (const [key, rows] of groups) {
        for (const row of rows) {
          const xVal = String(row[xAxis.field]);
          if (!merged.has(xVal)) merged.set(xVal, { [xAxis.field]: xVal });
          merged.get(xVal)![key] = row[yAxis.field];
        }
      }
      const mergedData = [...merged.values()];

      return (
        <div style={{ width: '100%', height: '100%', padding: containerPadding, minWidth: 0, minHeight: 0 }}>
          {!mini && titleEl}
          <ResponsiveContainer width="100%" height={mini ? '100%' : '95%'} minWidth={10} minHeight={10}>
            <BarChart data={mergedData} margin={margin}>
              {showGridHere && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
              <XAxis dataKey={xAxis.field} tick={tickStyle} />
              <YAxis tick={tickStyle} label={yAxisLabel} />
              {!mini && <Tooltip contentStyle={tooltipStyle} />}
              {showLegend && <Legend wrapperStyle={legendWrap} verticalAlign={legendAlign} />}
              {statLines}
              {allKeys.map((key, idx) => (
                <Bar key={key} dataKey={key} fill={getColor(pal, key, idx)} opacity={barOpacity}
                  stackId={config.barMode === 'stack' ? 'stack' : undefined}
                  isAnimationActive={!mini} animationDuration={300} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    case 'histogram': {
      const allValues = data.map((r) => Number(r[xAxis.field])).filter((v) => !isNaN(v));
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const bucketCount = histBins;
      const bucketSize = (max - min) / bucketCount || 1;

      const buckets: Record<string, unknown>[] = [];
      for (let i = 0; i < bucketCount; i++) {
        const lo = min + i * bucketSize;
        const hi = lo + bucketSize;
        const entry: Record<string, unknown> = { range: `${lo.toFixed(1)}` };
        for (const [key, rows] of groups) {
          entry[key] = rows.filter((r) => {
            const v = Number(r[xAxis.field]);
            return v >= lo && (i === bucketCount - 1 ? v <= hi : v < hi);
          }).length;
        }
        buckets.push(entry);
      }

      return (
        <div style={{ width: '100%', height: '100%', padding: containerPadding, minWidth: 0, minHeight: 0 }}>
          {!mini && titleEl}
          <ResponsiveContainer width="100%" height={mini ? '100%' : '95%'} minWidth={10} minHeight={10}>
            <BarChart data={buckets} margin={margin}>
              {showGridHere && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
              <XAxis dataKey="range" tick={tickStyle} />
              <YAxis tick={tickStyle} label={mini ? undefined : { value: 'Count', angle: -90, position: 'insideLeft', fill: textColor, fontSize: 12 }} />
              {!mini && <Tooltip contentStyle={tooltipStyle} />}
              {showLegend && <Legend wrapperStyle={legendWrap} verticalAlign={legendAlign} />}
              {[...groups.keys()].map((key, idx) => (
                <Bar key={key} dataKey={key} fill={getColor(pal, key, idx)} opacity={barOpacity} stackId="stack"
                  isAnimationActive={!mini} animationDuration={300} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    default:
      return <div style={{ padding: 20, color: textColor }}>Recharts: unsupported chart type &quot;{chartType}&quot;</div>;
  }
}
